import React from 'react';
import { Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { Home, ClipboardList, Package, Heart, User } from 'lucide-react';
import PhoneFrame from './PhoneFrame';
import { Welcome, Login, Register, Forgot } from './CustomerAuth';
import { HomeScreen, RestaurantList, Favorites, Profile, Support } from './CustomerHome';
import { RestaurantDetail, Cart } from './CustomerRestaurant';
import { Address, Payment, Confirm, Tracking, Completed } from './CustomerCheckout';
import { Orders, OrderDetail, MandaditosHome, NewMandadito, MandaditoTracking } from './CustomerOrders';
import PackageCheckout from './PackageCheckout';
import CustomerNotifications from './CustomerNotifications';
import { MyData, NotificationSettings, AccountSettings, LegalScreen } from './CustomerSettings';
import { OrderChat } from '../components/OrderChat';
import { useApp } from '../context/AppContext';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { CONTACT, waHref, WA_INCIDENT_MSG, SHOW_WHATSAPP } from '../lib/constants';
import { registerPush } from '../lib/native';

const TABS = [
  { to: '/app/home', label: 'Inicio', icon: Home },
  { to: '/app/orders', label: 'Pedidos', icon: ClipboardList },
  { to: '/app/mandaditos', label: 'Mandaditos', icon: Package },
  { to: '/app/favorites', label: 'Favoritos', icon: Heart },
  { to: '/app/profile', label: 'Perfil', icon: User },
];
const NAV_PATHS = ['/app/home', '/app/orders', '/app/mandaditos', '/app/favorites', '/app/profile', '/app/restaurants'];

export const BottomNav = () => (
  <nav className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around px-2 pt-2 pb-5 md:pb-4 z-30" style={{ paddingBottom: 'calc(1.25rem + var(--safe-bottom))' }}>
    {TABS.map((t) => (
      <NavLink key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase()}`} className={({ isActive }) => `flex flex-col items-center gap-1 text-[10px] font-medium w-14 ${isActive ? 'text-[#EE3B36]' : 'text-gray-500'}`}>
        {({ isActive }) => (<><t.icon size={22} strokeWidth={isActive ? 2.4 : 2} className={isActive ? 'fill-[#EE3B36]/15' : ''} />{t.label}</>)}
      </NavLink>
    ))}
  </nav>
);

const FAB_HIDE = /\/app\/(login|register|forgot|cart|address|payment|confirm|mandaditos\/new|encomiendas\/new)$/;

const WhatsAppFab = ({ raised }) => (
  <a
    href={waHref(CONTACT.whatsapp, WA_INCIDENT_MSG)}
    target="_blank"
    rel="noreferrer"
    data-testid="whatsapp-fab"
    aria-label="Reportar un incidente por WhatsApp"
    className={`absolute right-4 ${raised ? 'bottom-[86px]' : 'bottom-5'} z-40 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-600/40 active:scale-95 transition-transform`}
  >
    <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />
    <WhatsAppIcon className="w-7 h-7 relative" />
  </a>
);

const RequireCustomer = ({ children }) => {
  const { customer } = useApp();
  if (!customer) return <Navigate to="/app/login" replace />;
  return children;
};

const CustomerApp = () => {
  const { pathname } = useLocation();
  const { customer } = useApp();
  React.useEffect(() => { if (customer?.id) registerPush({ role: 'customer', userId: customer.id }); }, [customer?.id]);
  const showNav = NAV_PATHS.some((p) => pathname === p || pathname === `${p}/`);
  const showFab = SHOW_WHATSAPP && !!customer && pathname !== '/app' && !FAB_HIDE.test(pathname) && !pathname.startsWith('/app/chat/');
  return (
    <PhoneFrame>
      <Routes>
        <Route index element={<Welcome />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot" element={<Forgot />} />
        <Route path="home" element={<RequireCustomer><HomeScreen /></RequireCustomer>} />
        <Route path="restaurants" element={<RequireCustomer><RestaurantList /></RequireCustomer>} />
        <Route path="favorites" element={<RequireCustomer><Favorites /></RequireCustomer>} />
        <Route path="profile" element={<RequireCustomer><Profile /></RequireCustomer>} />
        <Route path="support" element={<RequireCustomer><Support /></RequireCustomer>} />
        <Route path="notificaciones" element={<RequireCustomer><CustomerNotifications /></RequireCustomer>} />
        <Route path="datos" element={<RequireCustomer><MyData /></RequireCustomer>} />
        <Route path="ajustes" element={<RequireCustomer><AccountSettings /></RequireCustomer>} />
        <Route path="ajustes/notificaciones" element={<RequireCustomer><NotificationSettings /></RequireCustomer>} />
        <Route path="legal/:slug" element={<RequireCustomer><LegalScreen /></RequireCustomer>} />
        <Route path="restaurant/:id" element={<RequireCustomer><RestaurantDetail /></RequireCustomer>} />
        <Route path="cart" element={<RequireCustomer><Cart /></RequireCustomer>} />
        <Route path="address" element={<RequireCustomer><Address /></RequireCustomer>} />
        <Route path="payment" element={<RequireCustomer><Payment /></RequireCustomer>} />
        <Route path="confirm" element={<RequireCustomer><Confirm /></RequireCustomer>} />
        <Route path="tracking/:id" element={<RequireCustomer><Tracking /></RequireCustomer>} />
        <Route path="completed/:id" element={<RequireCustomer><Completed /></RequireCustomer>} />
        <Route path="orders" element={<RequireCustomer><Orders /></RequireCustomer>} />
        <Route path="orders/:id" element={<RequireCustomer><OrderDetail /></RequireCustomer>} />
        <Route path="mandaditos" element={<RequireCustomer><MandaditosHome /></RequireCustomer>} />
        <Route path="mandaditos/new" element={<RequireCustomer><NewMandadito /></RequireCustomer>} />
        <Route path="encomiendas/new" element={<RequireCustomer><PackageCheckout /></RequireCustomer>} />
        <Route path="chat/:id" element={<RequireCustomer><OrderChat role="customer" /></RequireCustomer>} />
        <Route path="mandaditos/:id" element={<RequireCustomer><MandaditoTracking /></RequireCustomer>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
      {showFab && <WhatsAppFab raised={showNav} />}
      {showNav && <BottomNav />}
    </PhoneFrame>
  );
};

export default CustomerApp;
